import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import {
  Modal, Input, Select, Button, Table, Space, Divider, message, DatePicker, InputNumber,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import Rest from 'tools/rest';
import MaterialFormModal from './materialFormModal';
import IntegerQtyInput from './integerQtyInput';
import {
  materialDisplayUnit,
  parseMaterialIntegerQty,
  isQuantityExplicitZero,
  placementSchemaForWarehouse,
  buildLocationPayloadFromState,
  formatLocationDisplay,
} from '../utils';

const InvoiceForm = ({ visible, onClose, onSuccess }) => {
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [invoiceFileBlob, setInvoiceFileBlob] = useState(null);
  const [headerWarehouseId, setHeaderWarehouseId] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [registryMaterials, setRegistryMaterials] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [items, setItems] = useState([]);
  const [addQuantity, setAddQuantity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewMaterial, setShowNewMaterial] = useState(false);
  const fileInputRef = useRef(null);
  const fileDragDepthRef = useRef(0);
  const [fileDragOver, setFileDragOver] = useState(false);
  const submitLockRef = useRef(false);
  const [placementLoc, setPlacementLoc] = useState({});

  const headerWarehouse = useMemo(
    () => warehouses.find((w) => w.id === headerWarehouseId),
    [warehouses, headerWarehouseId]
  );
  const placementSchema = useMemo(
    () => placementSchemaForWarehouse(headerWarehouse),
    [headerWarehouse]
  );

  useEffect(() => {
    if (visible) {
      Rest.get('/api/v1/warehouses.json').then((r) =>
        setWarehouses(r.data.warehouses || [])
      );
      loadRegistryMaterials();
      setInvoiceName('');
      setInvoiceDate(null);
      setInvoiceFileBlob(null);
      setFileDragOver(false);
      fileDragDepthRef.current = 0;
      setHeaderWarehouseId(null);
      setItems([]);
      setSelectedMaterialId(null);
      setAddQuantity(null);
      submitLockRef.current = false;
      setPlacementLoc({});
    }
  }, [visible]);

  useEffect(() => {
    setPlacementLoc({});
  }, [headerWarehouseId]);

  const loadRegistryMaterials = () => {
    Rest.get('/api/v1/materials.json', { params: { per: 5000, page: 1 } })
      .then((r) => setRegistryMaterials(r.data.materials || []))
      .catch(() => {});
  };

  /** Только вид материала из реестра (наименование + категория), без складов и остатков */
  const materialOptionLabel = (m) => {
    const cat = m.category_name ? ` — ${m.category_name}` : '';
    return `${m.name}${cat}`;
  };

  const addItem = () => {
    if (!headerWarehouseId) {
      message.error('Сначала выберите склад поступления в шапке накладной');
      return;
    }
    if (!selectedMaterialId) {
      message.error('Выберите вид материала из реестра');
      return;
    }
    const mat = registryMaterials.find((m) => m.id === selectedMaterialId);
    if (!mat) return;

    if (isQuantityExplicitZero(addQuantity)) {
      message.error('Количество должно быть больше 0');
      return;
    }
    const q = parseMaterialIntegerQty(addQuantity);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }

    const locationJson = buildLocationPayloadFromState(placementLoc, placementSchema);

    setItems([...items, {
      rowKey: `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      material_id: mat.id,
      material_name: mat.name,
      category_name: mat.category_name,
      material_unit: mat.unit,
      unit: mat.unit,
      category_unit: mat.category_unit,
      qty: q,
      location: locationJson,
    }]);
    setSelectedMaterialId(null);
    setAddQuantity(null);
  };

  const removeItem = (rowKey) => setItems(items.filter((i) => i.rowKey !== rowKey));

  const updateItemQty = (rowKey, qty) => {
    setItems(items.map((i) => (i.rowKey === rowKey ? { ...i, qty } : i)));
  };

  const runMovesAfterInvoice = (invoiceResp) => {
    const inv = invoiceResp.data?.invoice;
    if (!inv?.id) {
      const errs = invoiceResp.data?.errors;
      const msg = errs && typeof errs === 'object'
        ? Object.values(errs).flat().join(', ')
        : 'Ошибка создания накладной';
      submitLockRef.current = false;
      setLoading(false);
      message.error(msg);
      return;
    }
    const invoiceId = inv.id;
    const wid = parseInt(headerWarehouseId, 10);

    const run = (idx) => {
      if (idx >= items.length) {
        message.success(`Накладная проведена (${items.length} поз.)`);
        submitLockRef.current = false;
        onSuccess();
        onClose();
        setLoading(false);
        return;
      }
      const row = items[idx];
      const qty = parseMaterialIntegerQty(row.qty);
      const mid = parseInt(row.material_id, 10);
      if (!Number.isFinite(wid) || !Number.isFinite(mid) || wid < 1 || mid < 1) {
        submitLockRef.current = false;
        setLoading(false);
        message.error('Некорректный склад или материал в строке накладной. Проверьте выбор склада и позиции.');
        return;
      }
      const locPayload = row.location && Object.keys(row.location).length
        ? row.location
        : {};

      Rest.post('/api/v1/warehouse_materials/ensure.json', {
        warehouse_material: {
          warehouse_id: wid,
          material_id: mid,
          location: locPayload,
        },
        warehouse_id: wid,
        material_id: mid,
      })
        .then((ensureResp) => {
          const wmId = ensureResp.data.warehouse_material.id;
          return Rest.post('/api/v1/material_moves.json', {
            material_move: {
              source_type: 'Invoice',
              source_id: invoiceId,
              destination_type: 'WarehouseMaterial',
              destination_id: wmId,
              material_id: row.material_id,
              quantity: qty,
              invoice_id: invoiceId,
            },
          });
        })
        .then(() => run(idx + 1))
        .catch((err) => {
          submitLockRef.current = false;
          setLoading(false);
          const d = err.response?.data;
          let detail = '';
          if (d && Array.isArray(d.errors)) detail = d.errors.join(', ');
          else if (d && d.errors && typeof d.errors === 'object') {
            detail = Object.values(d.errors).flat().join(', ');
          }
          message.error(
            detail
            || d?.error
            || `Ошибка на позиции «${row.material_name}»`
          );
        });
    };
    run(0);
  };

  const handleSubmit = () => {
    if (submitLockRef.current || loading) {
      return;
    }
    if (!invoiceName.trim()) {
      message.error('Введите название накладной');
      return;
    }
    if (!headerWarehouseId) {
      message.error('Выберите склад — по этой накладной всё поступление идёт на один склад');
      return;
    }
    if (items.length === 0) {
      message.error('Добавьте хотя бы одну позицию');
      return;
    }

    for (const row of items) {
      if (isQuantityExplicitZero(row.qty)) {
        message.error(`Для «${row.material_name}» укажите количество больше 0`);
        return;
      }
      const q = parseMaterialIntegerQty(row.qty);
      if (q === null) {
        message.error(`Некорректное количество для «${row.material_name}»`);
        return;
      }
    }

    submitLockRef.current = true;
    setLoading(true);

    const onInvoiceError = (err) => {
      submitLockRef.current = false;
      setLoading(false);
      const d = err.response?.data;
      let msg = d?.errors && typeof d.errors === 'object'
        ? Object.values(d.errors).flat().join(', ')
        : (Array.isArray(d?.errors) ? d.errors.join(', ') : null);
      if (!msg && err.response?.status === 500) {
        msg = 'Ошибка сервера при сохранении накладной (см. логи Rails).';
      }
      message.error(msg || 'Ошибка создания накладной');
    };

    const wid = parseInt(headerWarehouseId, 10);

    if (invoiceFileBlob) {
      const fd = new FormData();
      fd.append('invoice[name]', invoiceName.trim());
      fd.append('invoice[warehouse_id]', String(wid));
      if (invoiceDate) fd.append('invoice[invoice_date]', invoiceDate.format('YYYY-MM-DD'));
      fd.append('invoice[document]', invoiceFileBlob);
      Rest.postForm('/api/v1/invoices.json', fd)
        .then((invoiceResp) => {
          runMovesAfterInvoice(invoiceResp);
        })
        .catch(onInvoiceError);
    } else {
      const invoicePayload = {
        name: invoiceName.trim(),
        warehouse_id: wid,
        invoice_date: invoiceDate ? invoiceDate.format('YYYY-MM-DD') : null,
      };
      Rest.post('/api/v1/invoices.json', { invoice: invoicePayload })
        .then((invoiceResp) => {
          runMovesAfterInvoice(invoiceResp);
        })
        .catch(onInvoiceError);
    }
  };

  const handleRegistryMaterialCreated = (material) => {
    loadRegistryMaterials();
    setShowNewMaterial(false);
    if (material && material.id) {
      setSelectedMaterialId(material.id);
      message.success('Вид материала в реестре — укажите количество и «Добавить в ведомость»');
    }
  };

  const columns = [
    { title: 'Материал (реестр)', dataIndex: 'material_name', width: '22%' },
    {
      title: 'Категория',
      dataIndex: 'category_name',
      width: '14%',
      render: (v) => v || '—',
    },
    {
      title: 'Размещение',
      dataIndex: 'location',
      width: '18%',
      render: (loc) => formatLocationDisplay(loc, headerWarehouse?.temp_storage || {}),
    },
    {
      title: 'Ед. изм.',
      dataIndex: 'material_unit',
      width: '12%',
      render: (_, record) => materialDisplayUnit(record) || '—',
    },
    {
      title: 'Кол-во',
      dataIndex: 'qty',
      width: '22%',
      render: (val, record) => (
        <Space align="center" wrap>
          <IntegerQtyInput
            value={val}
            onChange={(v) => updateItemQty(record.rowKey, v)}
            style={{ width: 120 }}
          />
          {materialDisplayUnit(record) ? (
            <span style={{ color: '#888' }}>{materialDisplayUnit(record)}</span>
          ) : null}
        </Space>
      ),
    },
    {
      title: '',
      width: '8%',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.rowKey)}
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Поступление материалов (накладная)"
        open={visible}
        onCancel={onClose}
        width="85%"
        footer={[
          <Button key="cancel" onClick={onClose}>Отмена</Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            disabled={
              items.length === 0 || !invoiceName.trim() || !headerWarehouseId
            }
            onClick={handleSubmit}
          >
            Провести накладную ({items.length} поз.)
          </Button>,
        ]}
      >
        <Space wrap style={{ marginBottom: 16 }} align="start">
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Номер / название накладной</div>
            <Input
              value={invoiceName}
              onChange={(e) => setInvoiceName(e.target.value)}
              placeholder="Например, № 145 от поставщика"
              style={{ width: 280 }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Дата</div>
            <DatePicker
              value={invoiceDate}
              onChange={setInvoiceDate}
              format="DD.MM.YYYY"
              placeholder="Дата документа"
              style={{ width: 160 }}
              allowClear
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Склад поступления</div>
            <Select
              value={headerWarehouseId}
              onChange={setHeaderWarehouseId}
              placeholder="На какой склад пишем приход"
              style={{ width: 280 }}
              allowClear
              showSearch
              optionFilterProp="label"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
          </div>
        </Space>

        {headerWarehouseId && placementSchema.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Размещение на складе (для следующей строки ведомости)
            </div>
            <Space wrap align="start">
              {placementSchema.map(({ key, label }) => (
                <div key={key}>
                  <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>{label}</div>
                  <InputNumber
                    min={0}
                    placeholder="№"
                    style={{ width: 100 }}
                    value={placementLoc[key]}
                    onChange={(v) => setPlacementLoc((prev) => ({ ...prev, [key]: v }))}
                  />
                </div>
              ))}
            </Space>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>Файл накладной (при необходимости)</div>
          <div
              role="presentation"
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fileDragDepthRef.current += 1;
                setFileDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fileDragDepthRef.current -= 1;
                if (fileDragDepthRef.current <= 0) {
                  fileDragDepthRef.current = 0;
                  setFileDragOver(false);
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fileDragDepthRef.current = 0;
                setFileDragOver(false);
                const f = e.dataTransfer.files && e.dataTransfer.files[0];
                if (f) setInvoiceFileBlob(f);
              }}
              style={{
                border: `2px dashed ${fileDragOver ? '#1890ff' : '#d9d9d9'}`,
                borderRadius: 8,
                padding: 16,
                background: fileDragOver ? '#f0f7ff' : '#fafafa',
                maxWidth: 520,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) setInvoiceFileBlob(f);
                  e.target.value = '';
                }}
              />
              <Space wrap>
                <Button
                  type="default"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Выбрать файл
                </Button>
                <span style={{ fontSize: 12, color: '#888' }}>
                  или перетащите файл сюда
                </span>
              </Space>
              {invoiceFileBlob && (
                <div style={{ marginTop: 10, fontSize: 12 }}>
                  {invoiceFileBlob.name}
                  {' '}
                  <Button type="link" size="small" onClick={() => setInvoiceFileBlob(null)}>
                    убрать файл
                  </Button>
                </div>
              )}
            </div>
        </div>

        <Divider />

        <div style={{
          marginBottom: 16,
          padding: 16,
          backgroundColor: '#fafafa',
          borderRadius: 8,
          border: '1px solid #d9d9d9',
        }}
        >
          <Space wrap align="center">
            <div>
              <span style={{ marginRight: 8 }}>Вид материала (реестр):</span>
              <Select
                value={selectedMaterialId}
                onChange={setSelectedMaterialId}
                placeholder="Только наименование из реестра"
                style={{ width: 400 }}
                showSearch
                optionFilterProp="label"
                options={registryMaterials.map((m) => ({
                  value: m.id,
                  label: materialOptionLabel(m),
                }))}
              />
            </div>
            <div>
              <span style={{ marginRight: 8 }}>Кол-во:</span>
              <IntegerQtyInput
                value={addQuantity}
                onChange={setAddQuantity}
                style={{ width: 100 }}
                placeholder="Кол-во"
              />
            </div>
            <Button
              icon={<PlusOutlined />}
              onClick={addItem}
              disabled={!selectedMaterialId || !headerWarehouseId}
            >
              Добавить в ведомость
            </Button>
            <Button onClick={() => setShowNewMaterial(true)}>Новый вид в реестре</Button>
          </Space>
        </div>

        <Table
          bordered
          rowKey="rowKey"
          dataSource={items}
          columns={columns}
          size="small"
          pagination={false}
          scroll={{ y: 350 }}
          summary={(data) => {
            const total = data.reduce((s, r) => s + (parseMaterialIntegerQty(r.qty) || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <strong>Итого: {data.length} поз.</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}><strong>{total}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Modal>

      <MaterialFormModal
        visible={showNewMaterial}
        material={null}
        variant="registry"
        onClose={() => setShowNewMaterial(false)}
        onSuccess={handleRegistryMaterialCreated}
      />
    </>
  );
};

export default InvoiceForm;
